
import { memo, useRef, useState, useCallback, useEffect } from 'react';
import { Chip, Stack, Tooltip } from '@mui/material';
import { Tag } from 'types/Tag';

let canvas: HTMLCanvasElement | null = null;

function getTextWidth(text: string, font: string): number {
  if (!canvas) {
    canvas = document.createElement('canvas');
  }
  const context = canvas.getContext('2d');
  if (!context) return 0;
  context.font = font;
  const metrics = context.measureText(text);
  return metrics.width;
};

function estimateChipWidth(text: string, font: string): number {
  const textWidth = getTextWidth(text, font);
  const CHIP_PADDING = 14;
  return Math.ceil(textWidth + CHIP_PADDING);
};

export interface TagCellProps {
  tags: Tag[];
}

export const TagCell = memo(({ tags = [] }: TagCellProps) => {
  if (!tags || tags.length === 0) return null;

  const tagsRowRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(tags.length);
  const calculateVisibleTags = useCallback(() => {
    if (!tags || tags.length === 0) return;

  const tagsRow = tagsRowRef.current;
  if (!tagsRow) return;

  const containerWidth = tagsRow.offsetWidth;
  const computedStyle = window.getComputedStyle(tagsRow);
    const font = `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;
    const GAP = 8;
    const maxPlusText = `+${tags.length}`;
    const plusWidth = estimateChipWidth(maxPlusText, font);

    let totalWidth = 0;
    let count = 0;

    for (let i = 0; i < tags.length; i++) {
      const chipWidth = estimateChipWidth(tags[i].value, font);
      const gapWidth = i > 0 ? GAP : 0;
      const widthWithChip = totalWidth + gapWidth + chipWidth;
      const hasMore = i < tags.length - 1;

      if (hasMore) {
        const nextChipWidth = estimateChipWidth(tags[i + 1]?.value || '', font);
        const widthWithNextChip = widthWithChip + GAP + nextChipWidth;
        if (widthWithNextChip <= containerWidth) {
          totalWidth = widthWithChip;
          count++;
          continue;
        }
        const widthWithPlusChip = widthWithChip + GAP + plusWidth;
        if (widthWithPlusChip <= containerWidth) {
          totalWidth = widthWithChip;
          count++;
        }
        break;
      } else {
        if (widthWithChip <= containerWidth) {
          totalWidth = widthWithChip;
          count++;
        }
        break;
      }
    }
    setVisibleCount(count);
  }, [tags]);

  useEffect(() => {
    calculateVisibleTags();
    const resizeObserver = new ResizeObserver(calculateVisibleTags);
    if (tagsRowRef.current) {
      resizeObserver.observe(tagsRowRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [tags, calculateVisibleTags]);

  const visibleTags = tags.slice(0, visibleCount);
  const hiddenCount = tags.length - visibleCount;

  return (
    <Stack
  ref={tagsRowRef}
      direction="row"
      gap={1}
      overflow="hidden"
      flexWrap="nowrap"
      width="100%"
      height="100%"
      alignItems="center"
    >
      {visibleTags.map(tag => (
        <Chip key={tag.value} label={tag.value} size="small" />
      ))}
      {hiddenCount > 0 && (
        <Tooltip title={tags.slice(visibleCount).map(t => t.value).join(', ')}>
          <Chip
            label={`+${hiddenCount}`}
            size="small"
            variant="outlined"
          />
        </Tooltip>
      )}
    </Stack>
  );
});
